import { CenterWrapper } from '@layouts/CenterWrapper'
import { LoadingPage } from '@pages/loading-page'
import {
  Button,
  Card,
  Form,
  FormLayout,
  Link,
  Text,
  TextField,
} from '@shopify/polaris'
import { HELP_CENTER_HOME } from '@utils/helpLink'
import { buildLoginUrl } from '@utils/shopifyAuthFailure'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

function sanitizeShopDomain(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return ''
  return `${trimmed
    .replace(/https?:\/\//, '')
    .replace(/\/$/, '')
    .replace(/\.myshopify\.com$/, '')}.myshopify.com`
}

function getCookieValue(name: string): string {
  if (typeof document === 'undefined') return ''

  const cookie = document.cookie
    .split('; ')
    .find(item => item.startsWith(`${name}=`))

  if (!cookie) return ''

  const value = cookie.slice(name.length + 1)
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const InstallPage = (): React.ReactNode => {
  const location = useLocation()
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const initialShop = params.get('shop')?.replace(/\.myshopify\.com$/, '') ?? ''
  const [shopName, setShopName] = useState<string>(initialShop)
  const [shopError, setShopError] = useState<string>('')

  const hasEmbeddedShopifyContext = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const embedded = params.get('embedded')
    const host = params.get('host')
    const shop = params.get('shop')
    return Boolean(embedded && host && shop)
  }, [location.search])

  const shopFromQuery = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('shop')?.trim() ?? ''
    return raw
  }, [location.search])

  const shouldBackendLoginFromQuery =
    Boolean(shopFromQuery) && !hasEmbeddedShopifyContext

  // Preserve Reditus attribution query params across the redirect to the backend OAuth/login.
  // Otherwise the backend can't queue `session[:reditus_pending_conversion]`.
  const buildReferralTrackingQueryString = useCallback(() => {
    const query = new URLSearchParams()
    const red = params.get('red')
    const refby = params.get('refby')
    const grId = params.get('gr_id') || getCookieValue('_gr_id')

    if (red) query.set('red', red)
    if (refby) query.set('refby', refby)
    if (grId) query.set('gr_id', grId)
    return query.toString()
  }, [params])

  const buildBackendLoginUrl = useCallback(
    (shop: string) => {
      const base = buildLoginUrl({
        shop,
        requestedScopes: (params.get('requested_scopes') || '')
          .split(',')
          .filter(Boolean),
        returnTo: params.get('return_to'),
      })
      const referralTrackingQueryString = buildReferralTrackingQueryString()
      return referralTrackingQueryString
        ? `${base}&${referralTrackingQueryString}`
        : base
    },
    [buildReferralTrackingQueryString, params],
  )

  useEffect(() => {
    if (!shouldBackendLoginFromQuery) return
    const sanitized = sanitizeShopDomain(shopFromQuery)
    if (!sanitized) return
    const url = buildBackendLoginUrl(sanitized)
    window.location.assign(url)
  }, [shouldBackendLoginFromQuery, shopFromQuery, buildBackendLoginUrl])

  const handleSubmit = useCallback(() => {
    const trimmed = shopName.trim()
    if (!trimmed) {
      setShopError('Store name is required')
      return
    }

    const sanitized = sanitizeShopDomain(trimmed)
    const url = buildBackendLoginUrl(sanitized)

    window.location.href = url
  }, [shopName, buildBackendLoginUrl])

  const handleShopNameChange = useCallback((value: string) => {
    setShopName(value)
    setShopError('')
  }, [])

  if (hasEmbeddedShopifyContext) {
    return <Navigate to={{ pathname: '/', search: location.search }} replace />
  }

  if (shouldBackendLoginFromQuery) {
    return <LoadingPage title="Redirecting to login..." />
  }

  return (
    <CenterWrapper>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--p-space-400)',
        }}
      >
        <Card roundedAbove="lg">
          <Form onSubmit={handleSubmit}>
            <FormLayout>
              <Text variant="headingLg" as="h5">
                Welcome to PDF Invoice Generator
              </Text>
              <Text as="span">
                To get started, please enter your Shopify store name below.
              </Text>
              <TextField
                value={shopName}
                onChange={handleShopNameChange}
                labelHidden
                label="Shop Name"
                type="text"
                autoComplete="on"
                placeholder="example-store"
                requiredIndicator
                error={shopError || undefined}
                helpText={
                  <span>
                    Should not include <code>https://</code> or{' '}
                    <code>.myshopify.com</code>
                  </span>
                }
              />
              <Button submit>Login to App</Button>
            </FormLayout>
          </Form>
        </Card>
        <Text as="p" tone="subdued">
          Need help?{' '}
          <Link url={HELP_CENTER_HOME} target="_blank">
            Chat with Fordeer support
          </Link>
        </Text>
      </div>
    </CenterWrapper>
  )
}

export default InstallPage
